import { useEffect, useRef, useState } from "react";
import StreamVideo from "@/components/StreamVideo";
import { useParams, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import { ArrowLeft, Maximize2, RotateCw, Heart, X, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ShareButton from "@/components/ShareButton";
import RatingDialog from "@/components/RatingDialog";

// Detect iOS — its Safari uses non-standard webkit fullscreen and ignores
// screen.orientation.lock(). We hand off to the native player there which
// lets the user rotate the device themselves.
const IS_IOS = typeof navigator !== "undefined" && /iP(ad|hone|od)/.test(navigator.userAgent);

async function enterLandscapeFullscreen(videoEl) {
  if (!videoEl) return;
  try {
    if (IS_IOS && typeof videoEl.webkitEnterFullscreen === "function") {
      videoEl.webkitEnterFullscreen();
      return;
    }
    const wrapper = videoEl.parentElement || videoEl;
    if (wrapper.requestFullscreen) await wrapper.requestFullscreen();
    else if (wrapper.webkitRequestFullscreen) await wrapper.webkitRequestFullscreen();
    else if (videoEl.requestFullscreen) await videoEl.requestFullscreen();
    // Best-effort orientation lock — Android Chrome / Samsung Internet honour this.
    if (window.screen?.orientation?.lock) {
      try { await window.screen.orientation.lock("landscape"); }
      catch (e) { console.debug("[watch] orientation.lock unsupported", e?.message); }
    }
  } catch (e) {
    // Fullscreen rejected (user gesture missing, sandbox, etc.) — silent fall-through.
    console.debug("[watch] fullscreen request rejected", e?.message);
  }
}

export default function Watch() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [film, setFilm] = useState(null);
  const [error, setError] = useState(null);
  const [endedPromptOpen, setEndedPromptOpen] = useState(false);
  const [ratingOpen, setRatingOpen] = useState(false);
  const videoRef = useRef(null);
  const lastPingRef = useRef(0);
  // Only viewers can rate — filmmakers/admins hitting Watch see the share
  // prompt without the star CTA (they can't leave reviews on the API).
  const canRate = user?.role === "viewer";

  useEffect(() => {
    api.get(`/films/${id}`).then((r) => setFilm(r.data)).catch((e) => {
      setError(e.response?.data?.detail || "Cannot load film");
    });
  }, [id]);

  // Ping watch progress every ~10 seconds (and on pause)
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const ping = () => {
      const now = Math.floor(v.currentTime);
      if (now - lastPingRef.current >= 10) {
        lastPingRef.current = now;
        api.post(`/films/${id}/watch-progress`, { progress_seconds: now })
          .catch((err) => console.debug("[watch] progress ping failed", err?.response?.status));
      }
    };
    const onPause = () => {
      const now = Math.floor(v.currentTime);
      if (now > 0) api.post(`/films/${id}/watch-progress`, { progress_seconds: now })
        .catch((err) => console.debug("[watch] progress pause-ping failed", err?.response?.status));
    };
    // When the film ends naturally, trigger the share prompt — the highest-
    // intent moment for a viewer to evangelise. Has the same effect as
    // Spotify's end-of-album prompt or Netflix's "next episode" CTA, but for
    // organic growth.
    const onEnded = () => {
      onPause();
      setEndedPromptOpen(true);
    };
    v.addEventListener("timeupdate", ping);
    v.addEventListener("pause", onPause);
    v.addEventListener("ended", onEnded);
    return () => {
      v.removeEventListener("timeupdate", ping);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("ended", onEnded);
    };
  }, [film, id]);

  // Release the orientation lock when fullscreen ends so portrait pages work normally.
  useEffect(() => {
    const onFsChange = () => {
      const isFs = !!(document.fullscreenElement || document.webkitFullscreenElement);
      if (!isFs && window.screen?.orientation?.unlock) {
        try { window.screen.orientation.unlock(); }
        catch (e) { console.debug("[watch] orientation.unlock unsupported", e?.message); }
      }
    };
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
    };
  }, []);

  // The httpOnly access_token cookie carries auth for same-origin <video>
  // requests automatically. We never expose the JWT to JavaScript.
  const videoUrl = `${API}/films/${id}/video`;

  return (
    <div className="min-h-screen bg-ink">
      <Navbar />
      <div className="pt-24 px-4 sm:px-8 max-w-6xl mx-auto pb-12" data-testid="watch-page">
        <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-foreground/70 hover:text-foreground" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </Button>
        <h1 className="font-display text-3xl font-black mb-4">{film?.title || "Loading…"}</h1>
        <div className="aspect-video rounded-xl overflow-hidden bg-black border border-white/10 relative group">
          {error ? (
            <div className="w-full h-full flex items-center justify-center text-red-400 text-sm" data-testid="watch-error">{error}</div>
          ) : film ? (
            <StreamVideo
              ref={videoRef}
              key={videoUrl}
              playbackPath={`/films/${id}/playback`}
              legacySrc={videoUrl}
              controls
              controlsList="nodownload noremoteplayback"
              disablePictureInPicture
              disableRemotePlayback
              autoPlay
              playsInline
              preload="auto"
              className="w-full h-full"
              data-testid="film-video-player"
              onContextMenu={(e) => e.preventDefault()}
              onError={() => setError("Unable to play this video. Please try again.")}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-foreground/50">Loading…</div>
          )}
        </div>
        {film && !error && (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3" data-testid="watch-actions">
            <p className="text-xs text-foreground/55 sm:hidden flex items-center gap-1.5">
              <RotateCw className="w-3 h-3 text-terracotta" /> Tap fullscreen, then rotate your phone for the best view.
            </p>
            <Button
              onClick={() => enterLandscapeFullscreen(videoRef.current)}
              variant="outline"
              className="border-terracotta/40 text-terracotta hover:bg-terracotta/10 ml-auto h-10"
              data-testid="watch-fullscreen-btn"
            >
              <Maximize2 className="w-4 h-4" /> Fullscreen / landscape
            </Button>
            <ShareButton
              url={typeof window !== "undefined" ? `${window.location.origin}/api/share/film/${film.id}` : ""}
              title={`${film.title} · Cinevoya`}
              text={`Just watched "${film.title}" by ${film.filmmaker_name} on Cinevoya — short films from Africa.`}
              variant="outline"
              size="default"
              className="border-white/15 hover:bg-white/5 h-10"
              testId="watch-share-btn"
            />
          </div>
        )}
        {film?.synopsis && <p className="mt-6 text-foreground/75 leading-relaxed max-w-3xl">{film.synopsis}</p>}
      </div>

      {/* End-of-film prompt — highest-intent moment to both rate + share */}
      <Dialog open={endedPromptOpen} onOpenChange={setEndedPromptOpen}>
        <DialogContent className="bg-surface border-white/10 text-foreground sm:max-w-md" data-testid="ended-share-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Heart className="w-5 h-5 text-terracotta fill-terracotta" />
              Loved it?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-foreground/75 leading-relaxed">
              {canRate ? (
                <>Leave <strong className="text-foreground/95">{film?.filmmaker_name || "the filmmaker"}</strong> a rating — one honest review means the world to independent African filmmakers. Then share the film with a friend.</>
              ) : (
                <>Independent African filmmakers grow when their work travels.{" "}
                Send <strong className="text-foreground/95">{film?.title}</strong> to a friend who&apos;d love it too — every share lifts {film?.filmmaker_name || "the creator"}.</>
              )}
            </p>
            <div className="flex flex-wrap gap-2 justify-end">
              <Button
                variant="ghost"
                onClick={() => setEndedPromptOpen(false)}
                className="text-foreground/70 hover:bg-white/5"
                data-testid="ended-dismiss-btn"
              >
                <X className="w-4 h-4" /> Maybe later
              </Button>
              {canRate && (
                <Button
                  onClick={() => { setEndedPromptOpen(false); setRatingOpen(true); }}
                  className="bg-terracotta hover:bg-ember text-white"
                  data-testid="ended-rate-btn"
                >
                  <Star className="w-4 h-4" /> Rate this film
                </Button>
              )}
              {film && (
                <ShareButton
                  url={typeof window !== "undefined" ? `${window.location.origin}/api/share/film/${film.id}` : ""}
                  title={`${film.title} · Cinevoya`}
                  text={`Just watched "${film.title}" by ${film.filmmaker_name} on Cinevoya — you'll love this one.`}
                  variant={canRate ? "outline" : "default"}
                  className={canRate ? "border-white/15 hover:bg-white/5" : "bg-terracotta hover:bg-ember text-white"}
                  testId="ended-share-btn"
                />
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {film && (
        <RatingDialog
          open={ratingOpen}
          onOpenChange={setRatingOpen}
          film={film}
        />
      )}
    </div>
  );
}
